import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  Collapse,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../shared/hooks/redux';
import { signUp, clearError } from '../authSlice';
import GoogleSignInButton from '../components/GoogleSignInButton';

import { isFeatureEnabled } from '@/shared/libs/featureFlags';

// Autofill style override for Chrome
const autofillStyle = `
  input:-webkit-autofill,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
    box-shadow: 0 0 0 1000px #fff inset !important;
    -webkit-text-fill-color: #222 !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

export default function SignupPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(!isFeatureEnabled('googleLogin'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Clear validation error
    setValidationError('');
    if (!name || !email || !password || !confirmPassword) {
      setValidationError('All fields are required');
      return;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return;
    }
    try {
      await dispatch(signUp({ email, password, name })).unwrap();
      navigate('/curriculum');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      dispatch(clearError());
    }
    if (validationError) {
      setValidationError('');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) {
      dispatch(clearError());
    }
    if (validationError) {
      setValidationError('');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  return (
    <Container maxWidth="sm">
      {/* Autofill style override for Chrome */}
      <style>{autofillStyle}</style>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Sign Up
          </Typography>

          {(error || validationError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || validationError}
            </Alert>
          )}

          {isFeatureEnabled('googleLogin') && (
            <>
              <Box sx={{ mt: 2 }}>
                <GoogleSignInButton context="signup" />
              </Box>

              {!showEmailForm && (
                <Typography
                  variant="body2"
                  component="button"
                  onClick={() => setShowEmailForm(true)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'primary.main',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    p: 0,
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    marginTop: 2,
                  }}
                >
                  Sign up with email instead
                </Typography>
              )}
            </>
          )}

          <Collapse in={showEmailForm}>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Name"
                type="text"
                margin="normal"
                required
                value={name}
                onChange={handleNameChange}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                margin="normal"
                required
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                margin="normal"
                required
                value={password}
                onChange={handlePasswordChange}
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                margin="normal"
                required
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </Button>
            </Box>
          </Collapse>

          <Box textAlign="center" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link to="/login" style={{ textDecoration: 'none' }}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
